import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import type { AddressViewModel } from '@/models/patient';

import { useFocusOnMount } from '../../_shared/useFocusOnMount';

import { CountrySelect } from './CountrySelect';

interface AddressEditorProps {
  readonly address: AddressViewModel;
  readonly onSave: (next: AddressViewModel) => void;
  readonly onCancel: () => void;
}

interface AddressDraft {
  readonly line: string;
  readonly line2: string;
  readonly city: string;
  readonly district: string;
  readonly province: string;
  readonly postalCode: string;
  readonly country: string | null;
}

const toDraft = (record: AddressViewModel): AddressDraft => ({
  line: record.line ?? '',
  line2: record.line2 ?? '',
  city: record.city ?? '',
  district: record.district ?? '',
  province: record.province ?? '',
  postalCode: record.postalCode ?? '',
  country: record.country,
});

const nullify = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toModel = (d: AddressDraft): AddressViewModel => ({
  line: nullify(d.line),
  line2: nullify(d.line2),
  city: nullify(d.city),
  district: nullify(d.district),
  province: nullify(d.province),
  postalCode: nullify(d.postalCode),
  country: d.country,
});

export function AddressEditor({ address, onSave, onCancel }: AddressEditorProps): ReactElement {
  const { t } = useTranslation();
  const lineRef = useFocusOnMount<HTMLInputElement>();

  // PTV2-1327: the primitive owns the draft + dirty registration (warn on nav / view-mode / refresh
  // while this editor holds unsaved changes). Persistence is delegated to the parent card's `onSave`,
  // which also collapses this editor — so autosave stays OFF (an idle commit would tear the row down
  // mid-edit). The row is saved only by the explicit Confirm button (or the nav-guard teardown flush,
  // which commits through the same `onSave` — exactly the pre-1327 behaviour).
  const form = useEditableDraft<AddressDraft>({
    initial: toDraft(address),
    label: t('patient.demographics.addressFormLabel', { defaultValue: 'Address' }),
    autosave: false,
    onSave: (d) => {
      onSave(toModel(d));
    },
  });
  const { draft } = form;

  const submitAndClose = (): void => {
    onSave(toModel(draft));
  };

  return (
    <form
      ref={form.containerProps.ref}
      className="rounded-md border border-input bg-background p-3 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submitAndClose();
      }}
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') onCancel();
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <FieldLabel text={t('patient.demographics.addressLine')}>
          <Input
            ref={lineRef}
            value={draft.line}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, line: event.target.value }));
            }}
          />
        </FieldLabel>
        <FieldLabel text={t('patient.demographics.addressLine2')}>
          <Input
            value={draft.line2}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, line2: event.target.value }));
            }}
          />
        </FieldLabel>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FieldLabel text={t('patient.demographics.city')}>
          <Input
            value={draft.city}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, city: event.target.value }));
            }}
          />
        </FieldLabel>
        <FieldLabel text={t('patient.demographics.district')}>
          <Input
            value={draft.district}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, district: event.target.value }));
            }}
          />
        </FieldLabel>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <FieldLabel text={t('patient.demographics.province')}>
          <Input
            value={draft.province}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, province: event.target.value }));
            }}
          />
        </FieldLabel>
        <FieldLabel text={t('patient.demographics.postalCode')}>
          <Input
            value={draft.postalCode}
            className="h-8 text-sm"
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, postalCode: event.target.value }));
            }}
          />
        </FieldLabel>
        <FieldLabel text={t('patient.demographics.country')}>
          <CountrySelect
            value={draft.country}
            placeholder={t('patient.demographics.country')}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, country: next }));
            }}
          />
        </FieldLabel>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {t('ui.dialog.close')}
        </Button>
        <Button type="submit" size="sm">
          <Check className="h-4 w-4 mr-1.5" aria-hidden="true" />
          {t('patient.demographics.confirm')}
        </Button>
      </div>
    </form>
  );
}

interface FieldLabelProps {
  readonly text: string;
  readonly children: ReactNode;
}

function FieldLabel({ text, children }: FieldLabelProps): ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{text}</span>
      {children}
    </label>
  );
}
